import { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  children: ReactNode;
  /** Changing this resets the boundary — pass the route so navigation clears an error. */
  resetKey?: string;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Catches render errors so one bad component degrades to a message instead of a
 * blank page.
 *
 * Data from the API is not always the shape a component assumes — a projection
 * that omits a field, or a configurable key that has since been renamed. Those
 * should be fixed at the source, but they must not take the whole app down when
 * they happen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Render error:', error, info.componentStack);
    this.setState({ info });
  }

  componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null, info: null });
    }
  }

  render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          }
        >
          <AlertTitle>This view failed to render</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error.message}
          </Typography>

          {import.meta.env.DEV && info?.componentStack && (
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 1.5,
                maxHeight: 220,
                overflow: 'auto',
                fontSize: '0.6875rem',
                bgcolor: 'rgba(0,0,0,0.06)',
                borderRadius: 1,
              }}
            >
              {info.componentStack.trim()}
            </Box>
          )}
        </Alert>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => window.history.back()}>
            Go back
          </Button>
          <Button variant="contained" onClick={() => window.location.assign('/')}>
            Back to home
          </Button>
        </Stack>
      </Box>
    );
  }
}
